import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { getDataOfType } from '../../lib/utils/get-data-of-type'
import AppLoader from '../components/app-loader'
import { Routes, Route, Navigate } from 'react-router-dom'
import { routes } from './routes-config'
import type { RouteConfig } from './routes-config'
import Error404Page from '../pages/error-404-not-found'
import { PrivateRoute } from '../utilits'
import { B2BLayoutOutlet } from '../layouts/b2b'

export const DesktopRouter = memo(() => {
  const select = state => ({
    page: state.page,
    session: state.session,
  })
  const { page, session } = useSelector(select)
  const startPage = useMemo(() => {
    return getDataOfType(session, 'sessionData.start_page', String, '/orders')
  }, [session])

  const { publicRoutes, unrestrictedPrivateRoutes, restrictedRouteGroups } = useMemo(() => {
    const publicRoutesAccumulator: RouteConfig[] = []
    const unrestrictedAccumulator: RouteConfig[] = []
    const restrictedMap = new Map<string, { allowedRoles: number[]; items: RouteConfig[] }>()

    routes.forEach(route => {
      if (!route.private) {
        publicRoutesAccumulator.push(route)
        return
      }

      if (!route.allowedRoles || route.allowedRoles.length === 0) {
        unrestrictedAccumulator.push(route)
        return
      }

      const normalizedRoles = [...route.allowedRoles].sort((a, b) => a - b)
      const key = normalizedRoles.join(',')
      const current = restrictedMap.get(key)

      if (current) {
        current.items.push(route)
        return
      }

      restrictedMap.set(key, { allowedRoles: normalizedRoles, items: [route] })
    })

    return {
      publicRoutes: publicRoutesAccumulator,
      unrestrictedPrivateRoutes: unrestrictedAccumulator,
      restrictedRouteGroups: Array.from(restrictedMap.values()),
    }
  }, [])

  return (
    <>
      {page.isLoading && <AppLoader />}
      {!page.isLoading && (
        <Routes>
          {/* Корневой роут */}
          <Route
            path="/"
            element={
              session && session.sessionData && session.sessionData.user ? (
                <Navigate to={startPage} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          {/* Public routes */}
          {publicRoutes.map(r => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
          {/* Private routes */}
          <Route element={<PrivateRoute session={session} />}>
            <Route element={<B2BLayoutOutlet />}>
              {unrestrictedPrivateRoutes.map(route => (
                <Route key={route.path} path={route.path} element={route.element} />
              ))}
              {restrictedRouteGroups.map(group => (
                <Route
                  key={group.allowedRoles.join(',')}
                  element={<PrivateRoute session={session} allowedRoles={group.allowedRoles} />}
                >
                  {group.items.map(route => (
                    <Route key={route.path} path={route.path} element={route.element} />
                  ))}
                </Route>
              ))}
            </Route>
          </Route>
          {/* 404 */}
          <Route path="*" element={<Error404Page />} />
        </Routes>
      )}
    </>
  )
})

DesktopRouter.displayName = 'DesktopRouter'
